import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, Overline } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELD = "h-11 rounded-md border-forest-700/20 bg-white focus-visible:ring-terra";

const WINDOWS = [
  ["Morning (8am – 12pm)", "morning"],
  ["Afternoon (12pm – 4pm)", "afternoon"],
  ["Late afternoon (4pm – 7pm)", "late"],
];

export default function InspectionPage() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", preferred_date: "",
    time_window: "", system_interest: "", message: "", consent: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the consent box so we're allowed to contact you about your booking.");
      return;
    }
    setSending(true);
    try {
      await api.post("/inspections", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const windowLabel = WINDOWS.find(([, v]) => v === form.time_window)?.[0] || form.time_window;

  return (
    <div className="pt-28 sm:pt-32" data-testid="inspection-page">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-24 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline className="text-terra">Book a site inspection</Overline>
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-forest-700 sm:text-5xl" data-testid="inspection-title">
            Free, no obligation, and genuinely useful.
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-moss">
            We visit your home, measure up, check your switchboard and roof,
            and talk through the right system — then send a written quote with
            every rebate priced in.
          </p>
          <div className="mt-10 rounded-xl border border-forest-700/15 bg-bone-dark p-6" data-testid="inspection-what-happens">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-terra">What happens on the day</p>
            <ul className="mt-4 space-y-2.5 text-sm text-moss">
              <li>• We inspect your roof space, switchboard and existing system</li>
              <li>• We talk through sizing, brands and realistic running costs</li>
              <li>• You get a written quote within days — rebates already applied</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <FadeUp>
            {done ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-forest-700/15 bg-white px-8 py-20 text-center" data-testid="inspection-success">
                <CheckCircle2 className="h-14 w-14 text-forest-500" />
                <h2 className="font-display mt-5 text-2xl font-extrabold text-forest-700">Inspection requested</h2>
                <p className="mt-2 max-w-sm text-sm text-moss">
                  Thanks {form.name.split(" ")[0]} — we'll confirm your
                  preferred time shortly and send a calendar invite.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-xl border border-forest-700/15 bg-white p-7 sm:p-9" data-testid="inspection-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-name">Full name *</label>
                    <Input id="i-name" required minLength={2} className={FIELD} value={form.name} onChange={set("name")} data-testid="inspection-form-name" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-phone">Phone *</label>
                    <Input id="i-phone" required minLength={6} type="tel" className={FIELD} value={form.phone} onChange={set("phone")} data-testid="inspection-form-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-email">Email *</label>
                    <Input id="i-email" required type="email" className={FIELD} value={form.email} onChange={set("email")} data-testid="inspection-form-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-address">Street address *</label>
                    <Input id="i-address" required minLength={5} className={FIELD} value={form.address} onChange={set("address")} placeholder="Street, suburb, postcode" data-testid="inspection-form-address" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-date">Preferred date *</label>
                    <Input id="i-date" required type="date" className={FIELD} value={form.preferred_date} onChange={set("preferred_date")} data-testid="inspection-form-date" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700">Preferred time window *</label>
                    <Select required value={form.time_window} onValueChange={(v) => setForm((f) => ({ ...f, time_window: v }))}>
                      <SelectTrigger className={`${FIELD} w-full`} data-testid="inspection-form-window">
                        <SelectValue placeholder="Choose a window" />
                      </SelectTrigger>
                      <SelectContent>
                        {WINDOWS.map(([label, value]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700">System of interest</label>
                    <Select value={form.system_interest} onValueChange={(v) => setForm((f) => ({ ...f, system_interest: v }))}>
                      <SelectTrigger className={`${FIELD} w-full`} data-testid="inspection-form-system">
                        <SelectValue placeholder="Choose a system (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>
                        ))}
                        <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-forest-700" htmlFor="i-message">Anything we should know?</label>
                    <Textarea id="i-message" rows={3} className="rounded-md border-forest-700/20 bg-white focus-visible:ring-terra" value={form.message} onChange={set("message")} placeholder="Gate access, pets, existing solar…" data-testid="inspection-form-message" />
                  </div>
                  <div className="sm:col-span-2" data-testid="inspection-consent-block">
                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-moss">
                      <Checkbox
                        checked={form.consent}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                        className="mt-0.5 h-5 w-5 shrink-0 border-forest-700/40 data-[state=checked]:border-terra data-[state=checked]:bg-terra"
                        data-testid="inspection-form-consent"
                      />
                      <span>
                        I give Melwest Eco Energy consent to contact me regarding the
                        selected products and to get information about the rebates.{" "}
                        <span className="text-terra-dark">*</span>
                      </span>
                    </label>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="inspection-form-submit"
                  className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-terra px-7 text-sm font-bold text-bone hover:bg-terra-dark sm:w-auto"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {sending ? "Booking…" : "Book my free inspection"}
                </Button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
